import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import './Allocations.css';
import i18n from '../../i18n';
import AllocationsChart from './AllocationsChart';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEdit, faMinus, faPlus } from '@fortawesome/free-solid-svg-icons'
import { saveUpdatedNamesAndTickers, updateAllocationsDecided } from '../../actions/data'

const Allocations = ({ data, ...props }) => {
  const models = data.models;
  const START_INDEX = 3;
  const [allocationIndex, setAllocationIndex] = useState(Array.from({ length: data.selectedRiskProfileNumber }, (_, i) => i + START_INDEX));
  const selectedApproachModels = models.filter(model => model.modelFamily === data.selectedApproach)
    .map(model => ({ ...model, name: model.name.replace('WisdomTree', '').trim() }));

  const modelAssetClassWeights = selectedApproachModels.map(model => {
    let total = 0;
    const assetClassWeights = model.holdings.reduce((acc, holding, i) => {
      if (holding.assetClasses && holding.weights) {
        holding.assetClasses.forEach((assetClass, index) => {
          if (!acc[assetClass]) {
            acc[assetClass] = 0;
          }
          let weight = Number((Math.round(holding.weights[index] * 1000) / 10).toFixed(2));
          total += weight;
          if (i === model.holdings.length - 1 && index === holding.assetClasses.length - 1 && total !== 100) {
            weight += 100 - total;
          }
          acc[assetClass] += weight;
        });
      }
      return acc;
    }, {});

    for (let assetClass in assetClassWeights) {
      assetClassWeights[assetClass] = Math.round(assetClassWeights[assetClass]);
    }

    const sortedWeights = {
      'Equity': assetClassWeights['Equity'] || 0,
      'Fixed Income': assetClassWeights['Fixed Income'] || 0,
      ...assetClassWeights
    };

    const chartData = ['Equity', 'Fixed Income'].map(assetClass => sortedWeights[assetClass]);

    return {
      name: model.name,
      ticker: model.ticker,
      assetClassWeights: sortedWeights,
      chartData
    };
  }).sort((a, b) => b.assetClassWeights['Equity'] - a.assetClassWeights['Equity']);

  const selectedRiskProfileModels = allocationIndex.map(index => modelAssetClassWeights[index]);
  const [selectedModelNames, setSelectedModelNames] = useState(selectedRiskProfileModels.map(model => model.name));
  const [displayNames, setDisplayNames] = useState(selectedRiskProfileModels.map(model => model.name));
  const [isEditing, setIsEditing] = useState(selectedModelNames.map(() => false));

  const handleSelectChange = (event, index) => {
    const newSelectedModelNames = [...selectedModelNames];
    newSelectedModelNames[index] = event.target.value;
    setSelectedModelNames(newSelectedModelNames);

    const newDisplayNames = [...displayNames];
    newDisplayNames[index] = event.target.value;
    setDisplayNames(newDisplayNames);
  };

  const handleIconClick = (event, index) => {
    event.stopPropagation();
    const newIsEditing = isEditing.map((_, i) => i === index);
    setIsEditing(newIsEditing);
  };

  const handleInputChange = (event, index) => {
    const newDisplayNames = [...displayNames];
    newDisplayNames[index] = event.target.value;
    setDisplayNames(newDisplayNames);
  };

  const handleInputBlur = (index) => {
    const newIsEditing = [...isEditing];
    newIsEditing[index] = false;
    setIsEditing(newIsEditing);

    const newDisplayNames = [...displayNames];
    if (newDisplayNames[index].trim() === '') {
      newDisplayNames[index] = 'Enter a portfolio name';
    }
    setDisplayNames(newDisplayNames);
  };

  const handleKeyDown = (event, index) => {
    if (event.key === 'Delete') {
      const newDisplayNames = [...displayNames];
      newDisplayNames[index] = '';
      setDisplayNames(newDisplayNames);
    }
  };

  const handleMinus = (chartIndex) => {
    setAllocationIndex(prevIndex => prevIndex.map((value, index) => {
      if (index === chartIndex) {
        //const newIndex = (value + 1 + modelAssetClassWeights.length) % modelAssetClassWeights.length;
        const newIndex = ((value + 1) + modelAssetClassWeights.length) % modelAssetClassWeights.length;
        const newDisplayNames = [...displayNames];
        newDisplayNames[index] = modelAssetClassWeights[newIndex].name;
        setDisplayNames(newDisplayNames);
        return newIndex;
      }
      return value;
    }));
  };

  const handlePlus = (chartIndex) => {
    setAllocationIndex(prevIndex => prevIndex.map((value, index) => {
      if (index === chartIndex) {
        //const newIndex = (value - 1) % modelAssetClassWeights.length;
        const newIndex = ((value - 1) + modelAssetClassWeights.length) % modelAssetClassWeights.length;
        const newDisplayNames = [...displayNames];
        newDisplayNames[index] = modelAssetClassWeights[newIndex].name;
        setDisplayNames(newDisplayNames);
        return newIndex;
      }
      return value;
    }));
  };

  const handleSelectedRisks = () => {
    const selectedModels = allocationIndex.map(index => modelAssetClassWeights[index]);
    // const updatedNames = selectedModels.map(model => model.name);
    const updatedNames = displayNames;
    const tickers = selectedModels.map(model => model.ticker);
    console.log('new names', updatedNames);

    props.saveUpdatedNamesAndTickers(updatedNames, tickers);
    props.updateAllocationsDecided(true);
    window.scrollTo(141, 865);
  };

  return (
    <div>
      <div className='allocations-title'>{i18n.t('allocations')}</div>
      <div className='allocations-subtitle'>Please use the <FontAwesomeIcon icon={faPlus} size='sm' />  / <FontAwesomeIcon icon={faMinus} size='sm' /> to select asset class allocations and  <FontAwesomeIcon icon={faEdit} size='xs' /> to rename your portfolios.</div>

      <div className='chart-container-wrapper'>
        {selectedRiskProfileModels.map((_, index) => (
          <div key={index} className="chart-container">
            <div className='input-icon-container'>
              <input className='model-input' type="text" value={displayNames[index]} onChange={(event) => handleInputChange(event, index)} onKeyDown={(event) => handleKeyDown(event, index)} readOnly={!isEditing[index]} onBlur={() => handleInputBlur(index)} onClick={(event) => handleIconClick(event, index)} />
              <FontAwesomeIcon className={`edit-icon ${isEditing[index] ? 'editing' : ''}`} icon={faEdit} onClick={(event) => handleIconClick(event, index)} />
            </div>

            <AllocationsChart className='allocations-chart' data={modelAssetClassWeights[allocationIndex[index]].chartData} index={index} />

            <div className="stepper-container">
              <button className="stepper-button stepper-subtract" onClick={() => handleMinus(index)}><FontAwesomeIcon icon={faMinus} size='sm' /></button>
              <input className="stepper-input" readOnly value={`${modelAssetClassWeights[allocationIndex[index]].chartData[0]}% Equity | ${modelAssetClassWeights[allocationIndex[index]].chartData[1]}% Fixed Income`} />
              <button className="stepper-button stepper-add" onClick={() => handlePlus(index)}><FontAwesomeIcon icon={faPlus} size='sm' /></button>
            </div>
          </div>
        ))}
      </div>

      <div className='button-container'>
        <button className="allocationsNextButton" onClick={handleSelectedRisks}>Next</button>
      </div>
    </div>
  );
}

const mapStateToProps = state => ({
  data: state.data,
})

const mapDispatchToProps = dispatch => ({
  saveUpdatedNamesAndTickers: (updatedNames, tickers) =>
    dispatch(saveUpdatedNamesAndTickers(updatedNames, tickers)),
  updateAllocationsDecided: (value) =>
    dispatch(updateAllocationsDecided(value))
})

export default connect(mapStateToProps, mapDispatchToProps)(Allocations)